import React, { useState, useEffect } from 'react';
import './ProfessionalTab.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const emptyWork = { company: '', role: '', start: '', end: '' };
const emptyEducation = { institution: '', degree: '', start: '', end: '' };

const ProfessionalTab = ({ profile, setProfile, user, supabase, isOwnProfile }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    occupation: profile.occupation || '',
    professional_bio: profile.professional_bio || '', // <-- update here
    interests: profile.interests || [],
    skills: profile.skills || [],
    work: profile.work || [],
    education: profile.education || [],
    phone: profile.phone || '',
  });
  const [contactPrivacy, setContactPrivacy] = useState(profile.contactPrivacy || { email: false, phone: false });
  const [certificates, setCertificates] = useState(profile.certificates || []);
  const [newInterest, setNewInterest] = useState('');
  const [newSkill, setNewSkill] = useState('');
  const [workDraft, setWorkDraft] = useState(emptyWork);
  const [educationDraft, setEducationDraft] = useState(emptyEducation);
  const [uploading, setUploading] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [cvs, setCVs] = useState(profile.cvs || []);
  const [cvUploading, setCVUploading] = useState(false);

  // --- Handlers for tag fields ---
  const addTag = (field, value) => {
    if (!value.trim()) return;
    setForm(prev => ({
      ...prev,
      [field]: [...prev[field], value.trim()]
    }));
    field === 'interests' ? setNewInterest('') : setNewSkill('');
  };
  const removeTag = (field, idx) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== idx)
    }));
  };

  // --- Work/Education handlers ---
  const addWork = () => {
    if (!workDraft.company || !workDraft.role) return;
    setForm(prev => ({
      ...prev,
      work: [...prev.work, workDraft]
    }));
    setWorkDraft(emptyWork);
  };
  const removeWork = idx => {
    setForm(prev => ({
      ...prev,
      work: prev.work.filter((_, i) => i !== idx)
    }));
  };
  const addEducation = () => {
    if (!educationDraft.institution || !educationDraft.degree) return;
    setForm(prev => ({
      ...prev,
      education: [...prev.education, educationDraft]
    }));
    setEducationDraft(emptyEducation);
  };
  const removeEducation = idx => {
    setForm(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== idx)
    }));
  };

  // Ensure certificates are updated if profile changes (e.g., after reload)
  useEffect(() => {
    setCertificates(profile.certificates || []);
  }, [profile.certificates]);

  // Ensure CVs are updated if profile changes (e.g., after reload)
  useEffect(() => {
    setCVs(profile.cvs || []);
  }, [profile.cvs]);

  // --- Certificate upload/remove ---
  const handleCertificateUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const filePath = `${user.id}/${Date.now()}_${file.name}`;
    let { error } = await supabase.storage.from('documents').upload(filePath, file);
    if (!error) {
      const publicUrl = supabase.storage.from('documents').getPublicUrl(filePath).data.publicUrl;
      const updatedCertificates = [...certificates, { name: file.name, url: publicUrl }];
      setCertificates(updatedCertificates);

      // Save certificates to database immediately after upload
      await supabase
        .from('profiles')
        .update({ certificates: updatedCertificates })
        .eq('id', user.id);

      setProfile(prev => ({
        ...prev,
        certificates: updatedCertificates
      }));

      setNotification({ message: 'Certificate uploaded successfully!', type: 'success' });
    } else {
      setNotification({ message: 'Failed to upload certificate.', type: 'error' });
    }
    setUploading(false);
    setTimeout(() => setNotification({ message: '', type: '' }), 3500);
  };

  const removeCertificate = async idx => {
    const updatedCertificates = certificates.filter((_, i) => i !== idx);
    setCertificates(updatedCertificates);

    // Save updated certificates to database
    await supabase
      .from('profiles')
      .update({ certificates: updatedCertificates })
      .eq('id', user.id);

    setProfile(prev => ({
      ...prev,
      certificates: updatedCertificates
    }));
  };

  // --- CV upload/remove ---
  const handleCVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCVUploading(true);
    const filePath = `${user.id}/cv_${Date.now()}_${file.name}`;
    let { error } = await supabase.storage.from('documents').upload(filePath, file);
    if (!error) {
      const publicUrl = supabase.storage.from('documents').getPublicUrl(filePath).data.publicUrl;
      const updatedCVs = [...cvs, { name: file.name, url: publicUrl }];
      setCVs(updatedCVs);

      // Save CVs to database immediately after upload
      await supabase
        .from('profiles')
        .update({ cvs: updatedCVs })
        .eq('id', user.id);

      setProfile(prev => ({
        ...prev,
        cvs: updatedCVs
      }));

      setNotification({ message: 'CV uploaded successfully!', type: 'success' });
    } else {
      setNotification({ message: 'Failed to upload CV.', type: 'error' });
    }
    setCVUploading(false);
    setTimeout(() => setNotification({ message: '', type: '' }), 3500);
  };

  const removeCV = async idx => {
    const updatedCVs = cvs.filter((_, i) => i !== idx);
    setCVs(updatedCVs);

    // Save updated CVs to database
    await supabase
      .from('profiles')
      .update({ cvs: updatedCVs })
      .eq('id', user.id);

    setProfile(prev => ({
      ...prev,
      cvs: updatedCVs
    }));
  };

  // --- Contact privacy ---
  const toggleContactPrivacy = async (field) => {
    const newPrivacy = {
      ...contactPrivacy,
      [field]: !contactPrivacy[field]
    };
    setContactPrivacy(newPrivacy);
    await supabase
      .from('profiles')
      .update({ contactPrivacy: newPrivacy })
      .eq('id', user.id);
    await supabase.auth.updateUser({
      data: { contactPrivacy: newPrivacy }
    });
    setProfile(prev => ({
      ...prev,
      contactPrivacy: newPrivacy
    }));
  };

  // --- Save/Cancel ---
  const handleSave = async () => {
    // Validate and normalize phone
    let phoneE164 = form.phone || null;
    let phoneCountryCode = null;
    if (phoneE164) {
      if (!isValidPhoneNumber(phoneE164)) {
        alert('Please enter a valid phone number.');
        return;
      }
      const p = parsePhoneNumberFromString(phoneE164);
      if (p) phoneCountryCode = `+${p.countryCallingCode}`;
    }

    const updatedProfile = {
      ...profile,
      occupation: form.occupation,
      professional_bio: form.professional_bio, // <-- update here
      interests: form.interests,
      skills: form.skills,
      work: form.work,
      education: form.education,
      certificates,
      cvs,
      phone: phoneE164,
      phone_country_code: phoneCountryCode,
    };
    await supabase
      .from('profiles')
      .update(updatedProfile)
      .eq('id', user.id);
    setProfile(updatedProfile);
    setEditing(false);
  };

  const handleCancel = () => {
    setForm({
      occupation: profile.occupation || '',
      professional_bio: profile.professional_bio || '', // <-- fixed key here
      interests: profile.interests || [],
      skills: profile.skills || [],
      work: profile.work || [],
      education: profile.education || [],
      phone: profile.phone || ''
    });
    setCertificates(profile.certificates || []);
    setCVs(profile.cvs || []);
    setEditing(false);
  };

  // --- Render ---
  return (
    <div className="profile-section professional-section">
      {/* Notification */}
      {notification.message && (
        <div className={`prof-notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* 1. Professional Summary */}
      <div className="card prof-summary-card">
        <h2>Professional Summary</h2>
        {editing ? (
          <>
            <label className="input-label" htmlFor="occupation-input">Occupation</label>
            <input
              id="occupation-input"
              name="occupation"
              value={form.occupation}
              onChange={e => setForm({ ...form, occupation: e.target.value })}
              placeholder="Occupation"
              className="occupation-input"
            />
            <label className="input-label" htmlFor="bio-textarea">Professional Bio</label>
            <textarea
              id="bio-textarea"
              name="professional_bio"
              value={form.professional_bio}
              onChange={e => setForm({ ...form, professional_bio: e.target.value })}
              placeholder="Professional Bio"
              className="bio-textarea"
              rows={5}
            />
          </>
        ) : (
          <>
            <div className="occupation">{profile.occupation || <span className="profile-muted">No occupation provided.</span>}</div>
            <div className="professional-bio">{profile.professional_bio || <span className="profile-muted">No professional bio provided.</span>}</div>
          </>
        )}
      </div>

      {/* 2. Interests & Skills */}
      <div className="card interests-skills-card">
        <div className="row">
          <div className="col">
            <h3>Interests</h3>
            <div className="tag-list">
              {(editing ? form.interests : profile.interests || []).map((interest, idx) => (
                <span className="tag" key={idx}>
                  {interest}
                  {editing && <button className="tag-remove" onClick={() => removeTag('interests', idx)}>×</button>}
                </span>
              ))}
              {editing && (
                <input
                  className="tag-input"
                  value={newInterest}
                  onChange={e => setNewInterest(e.target.value)}
                  onKeyDown={e => (e.key === 'Enter' ? addTag('interests', newInterest) : null)}
                  placeholder="Add interest"
                />
              )}
            </div>
          </div>
          <div className="col">
            <h3>Skills</h3>
            <div className="tag-list">
              {(editing ? form.skills : profile.skills || []).map((skill, idx) => (
                <span className="tag" key={idx}>
                  {skill}
                  {editing && <button className="tag-remove" onClick={() => removeTag('skills', idx)}>×</button>}
                </span>
              ))}
              {editing && (
                <input
                  className="tag-input"
                  value={newSkill}
                  onChange={e => setNewSkill(e.target.value)}
                  onKeyDown={e => (e.key === 'Enter' ? addTag('skills', newSkill) : null)}
                  placeholder="Add skill"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 3. Work Experience */}
      <div className="card work-card">
        <h2>Work Experience</h2>
        <ul className="work-list">
          {(editing ? form.work : profile.work || []).map((job, idx) => (
            <li key={idx}>
              <span>{job.role} at {job.company} ({job.start} - {job.end || 'Present'})</span>
              {editing && <button className="tag-remove" onClick={() => removeWork(idx)}>×</button>}
            </li>
          ))}
        </ul>
        {editing && (
          <div className="work-form">
            <input placeholder="Company" value={workDraft.company} onChange={e => setWorkDraft({ ...workDraft, company: e.target.value })} />
            <input placeholder="Role" value={workDraft.role} onChange={e => setWorkDraft({ ...workDraft, role: e.target.value })} />
            <input placeholder="Start" value={workDraft.start} onChange={e => setWorkDraft({ ...workDraft, start: e.target.value })} />
            <input placeholder="End" value={workDraft.end} onChange={e => setWorkDraft({ ...workDraft, end: e.target.value })} />
            <button className="add-btn" onClick={addWork}>Add</button>
          </div>
        )}
      </div>

      {/* 4. Education */}
      <div className="card education-card">
        <h2>Education</h2>
        <ul className="education-list">
          {(editing ? form.education : profile.education || []).map((edu, idx) => (
            <li key={idx}>
              <span>{edu.degree} at {edu.institution} ({edu.start} - {edu.end || 'Present'})</span>
              {editing && <button className="tag-remove" onClick={() => removeEducation(idx)}>×</button>}
            </li>
          ))}
        </ul>
        {editing && (
          <div className="education-form">
            <input placeholder="Institution" value={educationDraft.institution} onChange={e => setEducationDraft({ ...educationDraft, institution: e.target.value })} />
            <input placeholder="Degree" value={educationDraft.degree} onChange={e => setEducationDraft({ ...educationDraft, degree: e.target.value })} />
            <input placeholder="Start" value={educationDraft.start} onChange={e => setEducationDraft({ ...educationDraft, start: e.target.value })} />
            <input placeholder="End" value={educationDraft.end} onChange={e => setEducationDraft({ ...educationDraft, end: e.target.value })} />
            <button className="add-btn" onClick={addEducation}>Add</button>
          </div>
        )}
      </div>

      {/* 5. Certificates & Degrees */}
      <div className="card certificates-card">
        <h2>Certificates & Degrees</h2>
        <div className="doc-grid">
          {certificates.length === 0 && (
            <div>
              <span className="profile-muted">No certificates or degrees uploaded.</span>
            </div>
          )}
          {[...certificates]
            .sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0))
            .map((cert, idx) => {
              const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(cert.name);
              const isPDF = /\.pdf$/i.test(cert.name);
              return (
                <div key={idx} className="doc-thumbnail-item">
                  <a href={cert.url} target="_blank" rel="noopener noreferrer" className="certificate-link">
                    {isImage ? (
                      <img src={cert.url} alt={cert.name} className="doc-thumbnail" />
                    ) : isPDF ? (
                      <img src="/pdf-icon.png" alt="PDF" className="doc-thumbnail" />
                    ) : (
                      <img src="/file-icon.png" alt="File" className="doc-thumbnail" />
                    )}
                    <span className="doc-name">{cert.name}</span>
                  </a>
                  {editing && <button className="tag-remove" onClick={() => removeCertificate(idx)}>×</button>}
                </div>
              );
            })}
        </div>
        {editing && (
          <div className="upload-certificates">
            <label className="upload-label">
              <span className="upload-btn">Upload Certificate</span>
              <input type="file" onChange={handleCertificateUpload} disabled={uploading} style={{ display: 'none' }} />
            </label>
            {uploading && <span>Uploading...</span>}
          </div>
        )}
      </div>

      {/* 5b. CVs */}
      <div className="card cvs-card">
        <h2>CVs</h2>
        <div className="doc-grid">
          {cvs.length === 0 && (
            <div>
              <span className="profile-muted">No CVs uploaded.</span>
            </div>
          )}
          {[...cvs]
            .sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0))
            .map((cv, idx) => {
              const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(cv.name);
              const isPDF = /\.pdf$/i.test(cv.name);
              return (
                <div key={idx} className="doc-thumbnail-item">
                  <a href={cv.url} target="_blank" rel="noopener noreferrer" className="certificate-link">
                    {isImage ? (
                      <img src={cv.url} alt={cv.name} className="doc-thumbnail" />
                    ) : isPDF ? (
                      <img src="/pdf-icon.png" alt="PDF" className="doc-thumbnail" />
                    ) : (
                      <img src="/file-icon.png" alt="File" className="doc-thumbnail" />
                    )}
                    <span className="doc-name">{cv.name}</span>
                  </a>
                  {editing && <button className="tag-remove" onClick={() => removeCV(idx)}>×</button>}
                </div>
              );
            })}
        </div>
        {editing && (
          <div className="upload-cvs">
            <label className="upload-label">
              <span className="upload-btn">Upload CV</span>
              <input type="file" onChange={handleCVUpload} disabled={cvUploading} style={{ display: 'none' }} />
            </label>
            {cvUploading && <span>Uploading...</span>}
          </div>
        )}
      </div>

      {/* 6. Contact Information */}
      <div className="card contact-card">
        <h2>Contact Information</h2>
        <div className="contact-item">
          <span className="contact-label">Email:</span>
          <span className="contact-value">{profile.email}</span>
          <button
            className="privacy-toggle"
            onClick={() => toggleContactPrivacy('email')}
            title={contactPrivacy.email ? 'Make email private' : 'Make email public'}
          >
            {contactPrivacy.email ? <span role="img" aria-label="lock">🔒</span> : <span role="img" aria-label="unlock">🔓</span>}
          </button>
        </div>
        <div className="contact-item">
          <span className="contact-label">Phone:</span>
          {editing ? (
            <PhoneInput
              international
              defaultCountry="US"
              value={form.phone || ''}
              onChange={(value) => setForm({ ...form, phone: value || '' })}
              id="prof-phone"
              name="phone"
            />
          ) : (
            <span className="contact-value">{profile.phone || 'Not provided'}</span>
          )}
          <button
            className="privacy-toggle"
            onClick={() => toggleContactPrivacy('phone')}
            title={contactPrivacy.phone ? 'Make phone private' : 'Make phone public'}
          >
            {contactPrivacy.phone ? <span role="img" aria-label="lock">🔒</span> : <span role="img" aria-label="unlock">🔓</span>}
          </button>
        </div>
      </div>

      {/* Edit/Save/Cancel buttons */}
      {isOwnProfile && (
        editing ? (
          <div className="edit-btn-row">
            <button className="edit-btn" onClick={handleSave}>Save</button>
            <button className="edit-btn" onClick={handleCancel}>Cancel</button>
          </div>
        ) : (
          <button className="edit-btn" onClick={() => setEditing(true)}>Edit</button>
        )
      )}
    </div>
  );
};

export default ProfessionalTab;